import { Injectable, inject } from '@angular/core';
import { IFiDialogResult } from '@fi-app/shared/fi-dialogs/fi-dialogs';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiItemsListSaveFilterDialogComponent } from '../fi-items-list-filter/fi-items-list-save-filter-dialog/fi-items-list-save-filter-dialog.component';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { ISelectedFilterItem } from '../fi-items-list-filter/fi-items-list-filter';
import { FiItemsListEditFilterDialogComponent } from '../fi-items-list-filter/fi-items-list-edit-filter-dialog/fi-items-list-edit-filter-dialog.component';

@Injectable()
export class ItemsListFilterDialogSnackbarService {
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);
    private fiSnackBarService = inject(FiSnackBarService);

    getFilterNameDialog(filterName: string, headerText: string, headerIconClass: string): IFiDialogResult<string> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiItemsListSaveFilterDialogComponent,
            contentComponentData: { filterName },
            headerText: headerText,
            messageText: this.appTextService.get('save_filter_dialog_description'),
            yesButtonText: this.appTextService.get('save'),
            isTopCloseButtonShown: true,
            headerIconClass: headerIconClass,
            config: {
                panelClass: 'fi-items-list-save-filter-dialog-panel',
                closeOnNavigation: false,
            },
        });
    }

    getConfirmDeleteSavedFilterDialog(): IFiDialogResult {
        return this.fiDialogsService.confirmSimpleDelete({
            headerText: this.appTextService.get('filter.delete_filter'),
            messageText: this.appTextService.get('filter.delete_filter_description'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-confirm-delete-dialog-panel'],
                autoFocus: false,
            },
        });
    }

    getSaveFilterDialog(
        filterName: string,
        headerText?: string,
        headerIconClass?: string,
        description?: string,
    ): IFiDialogResult<string> {
        return this.fiDialogsService.custom({
            isTopCloseButtonShown: true,
            noButtonText: this.appTextService.get('common.cancel'),
            yesButtonText: this.appTextService.get('common.save'),
            headerText: headerText || this.appTextService.get('save_filter'),
            headerIconClass: headerIconClass || 'far fa-bookmark BrandedColor',
            preventUppercaseButtonLabel: true,
            contentComponentRef: FiItemsListSaveFilterDialogComponent,
            contentComponentData: {
                filterName: filterName,
                description: description || this.appTextService.get('save_filter.description'),
            },
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                width: '500px',
                closeOnNavigation: false,
            },
        });
    }

    getEditFilterDialog(
        filterName: string,
        selectedItems: ISelectedFilterItem[],
    ): IFiDialogResult<ISelectedFilterItem[]> {
        return this.fiDialogsService.custom({
            headerText: filterName,
            headerIconClass: 'fal fa-bookmark',
            isTopCloseButtonShown: true,
            messageText: this.appTextService.get('filter.update_description'),
            yesButtonText: this.appTextService.get('common.save'),
            contentComponentData: [...selectedItems],
            contentComponentRef: FiItemsListEditFilterDialogComponent,
            preventUppercaseButtonLabel: true,
            config: {
                autoFocus: false,
                panelClass: 'fi-rounded-dialog-panel',
            },
        });
    }

    getNoFilterItemsWarningDialog(): IFiDialogResult {
        return this.fiDialogsService.confirm({
            headerText: this.appTextService.get('filter.delete_warning_dialog_header'),
            messageText: this.appTextService.get('filter.delete_warning_dialog_message'),
            yesButtonText: this.appTextService.get('common.delete'),
            headerIconClass: 'fal fa-circle-exclamation BrandedColor',
            isTopCloseButtonShown: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        });
    }

    getSaveFilterAsWidgetDialog(
        headerText: string,
        widgetDescription: string,
        widgetName?: string,
    ): IFiDialogResult<string> {
        return this.fiDialogsService.custom({
            isTopCloseButtonShown: true,
            noButtonText: this.appTextService.get('common.cancel'),
            yesButtonText: this.appTextService.get('common.save'),
            headerText: headerText,
            headerIconClass: 'far fa-bookmark BrandedColor',
            preventUppercaseButtonLabel: true,
            contentComponentRef: FiItemsListSaveFilterDialogComponent,
            contentComponentData: {
                filterName: widgetName,
                description: widgetDescription,
                label: this.appTextService.get('widget.name'),
                hint: this.appTextService.get('widget.name_hint'),
            },
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                width: '500px',
                closeOnNavigation: false,
            },
        });
    }

    getConfirmWidgetUpdateDialog(): IFiDialogResult<unknown> {
        return this.fiDialogsService.confirm({
            isBigSizeTopBorder: true,
            headerText: this.appTextService.get('common.update_widget'),
            headerIconClass: 'far fa-grid-2 BrandedColor',
            messageText: this.appTextService.get('widget.update_widget_description'),
            yesButtonText: this.appTextService.get('common.save'),
            noButtonText: this.appTextService.get('common.cancel'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
                restoreFocus: false,
                maxWidth: '90%',
            },
        });
    }

    private showBrandedInfoSnackbar(textMessage: string): void {
        this.fiSnackBarService.showSnackbar(
            {
                textMessage: textMessage,
                iconClass: 'fas fa-circle-info BrandedColor',
                buttonText: this.appTextService.get('common.dismiss'),
                isSnackbarBorderShown: true,
                buttonClass: 'BrandedColor',
            },
            true,
            'fi-snackbar-standard',
        );
    }

    showWidgetUpdatedSnackbar(): void {
        this.showBrandedInfoSnackbar(this.appTextService.get('widget.update_success'));
    }

    showWidgetAddedSnackbar(): void {
        this.showBrandedInfoSnackbar(this.appTextService.get('widget.save_success'));
    }

    showFilterUpdatedSnackbar(filterName: string): void {
        this.showBrandedInfoSnackbar(this.appTextService.get('filter.filter_has_been_updated', { '{0}': filterName }));
    }

    showFilterSavedSnackbar(filterName: string): void {
        this.showBrandedInfoSnackbar(
            this.appTextService.get('filter.save_filter_success_snackbar_message', { '{0}': filterName }),
        );
    }

    showSaveSuccessSnackbar(filterName: string) {
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('filter.save_filter_success_snackbar_message', { '{0}': filterName }),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    showUpdateSuccessSnackbar(filterName: string) {
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('filter.update_filter_success_snackbar_message', {
                '{0}': filterName,
            }),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }
}
